// @system — Share row under the article: copy link (brand primary), X and LinkedIn intents.
import { useState } from 'react'
import { Link2, Check } from 'lucide-react'
import { Button } from '@/app/components/@system/ui/button'
import { text } from '@/config'

/**
 * @typedef {object} ShareRowProps
 * @property {string} url    Absolute URL of the article
 * @property {string} title
 */

/** @param {ShareRowProps} props */
export function ShareRow({ url, title }) {
  const [copied, setCopied] = useState(false)
  const t = text.blog.share
  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(title)

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard unavailable in a non-secure context — the URL is still in the address bar
    }
  }

  return (
    <div className="mt-8 flex flex-wrap items-center gap-2" role="group" aria-label={t.label}>
      <span className="mr-1 text-sm text-brand-text-muted">{t.label}</span>
      <Button type="button" size="sm" className="min-h-touch" onClick={copy} iconLeft={copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Link2 className="h-4 w-4" aria-hidden="true" />}>
        {copied ? t.copied : t.copy}
      </Button>
      <Button variant="outline" size="sm" className="min-h-touch" asChild>
        <a href={`https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`} target="_blank" rel="noopener noreferrer">
          {t.x}
        </a>
      </Button>
      <Button variant="outline" size="sm" className="min-h-touch" asChild>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer">
          {t.linkedin}
        </a>
      </Button>
      <span className="sr-only" role="status" aria-live="polite">{copied ? t.copied : ''}</span>
    </div>
  )
}
